import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { attendanceApi } from '../utils/api';

const AttendanceContext = createContext(null);

const storageKey = (userId) => `crm_attendance_clocked_in_${userId}`;

const readStoredClockedIn = (userId) => {
  if (!userId) return null;
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    // ignore
  }
  return null;
};

const writeStoredClockedIn = (userId, value) => {
  if (!userId) return;
  try {
    sessionStorage.setItem(storageKey(userId), value ? '1' : '0');
  } catch {
    // ignore
  }
};

export function AttendanceProvider({ children }) {
  const { user, token, isAuthenticated } = useAuth();
  const userId = user?.id;

  const [isClockedIn, setIsClockedIn] = useState(() => readStoredClockedIn(userId) ?? false);

  const refreshAttendanceStatus = useCallback(async () => {
    if (!userId || !token || !isAuthenticated) {
      if (!userId) setIsClockedIn(false);
      return;
    }

    try {
      const res = await attendanceApi.getToday();
      const data = res?.data || res || {};
      const current = data.current_clock_in || data.currentClockIn;
      const next = Boolean(current?.id || current);
      setIsClockedIn(next);
      writeStoredClockedIn(userId, next);
    } catch {
      // در خطا وضعیت کش‌شده حفظ می‌شود تا UI پرش نداشته باشد
    }
  }, [userId, token, isAuthenticated]);

  useEffect(() => {
    if (!userId || !token || !isAuthenticated) {
      if (!userId) setIsClockedIn(false);
      return;
    }
    const cached = readStoredClockedIn(userId);
    if (cached !== null) {
      setIsClockedIn(cached);
    }
    refreshAttendanceStatus();
  }, [userId, token, isAuthenticated, refreshAttendanceStatus]);

  const value = useMemo(
    () => ({
      isClockedIn,
      refreshAttendanceStatus,
    }),
    [isClockedIn, refreshAttendanceStatus],
  );

  return (
    <AttendanceContext.Provider value={value}>{children}</AttendanceContext.Provider>
  );
}

export const useAttendance = () => {
  const ctx = useContext(AttendanceContext);
  if (!ctx) {
    throw new Error('useAttendance must be used within AttendanceProvider');
  }
  return ctx;
};
